import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Ic from "../icons.jsx";
import { useToast } from "../Toast";

const DEFAULT_ALLOWED = ["image/jpeg", "image/png", "application/pdf"];
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;
const DEFAULT_MAX_FILES = 5;

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  /** Allowed MIME types. Defaults to JPG/PNG/PDF. */
  allowedTypes?: string[];
  /** Max bytes per file. Default 10 MB. */
  maxBytes?: number;
  /** Max number of staged files. Default 5. */
  maxFiles?: number;
  /** Override the type-list line shown under the picker. */
  fileTypesHint?: string;
  /**
   * i18n key prefix (including trailing dot) for this instance's copy —
   * `{prefix}errMax`, `{prefix}errType`, `{prefix}errTooLarge`, `{prefix}atLimit`,
   * `{prefix}add`, `{prefix}fileTypes`. Defaults to the shared, customer-facing
   * `attachments.` namespace (translated in all 10 locales). Internal-only
   * callers (e.g. the TM module, EN+SR only) pass their own prefix so their
   * existing keys stay under the parity script's internal-only exemption
   * instead of requiring 10-locale translations.
   */
  i18nPrefix?: string;
};

/**
 * Pre-submit attachment stager, shared across every module that lets a user
 * pick files before the parent entity exists yet (TM tasks,
 * complaints, …). Holds File objects in memory only; the actual MinIO upload
 * happens after the parent creates the entity and a real parent id exists —
 * see each caller's `create → presign → PUT → confirm` upload flow.
 */
export function LocalAttachmentStager({
  files, onChange, disabled,
  allowedTypes = DEFAULT_ALLOWED,
  maxBytes = DEFAULT_MAX_BYTES,
  maxFiles = DEFAULT_MAX_FILES,
  fileTypesHint,
  i18nPrefix = "attachments.",
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const maxMb = Math.round(maxBytes / (1024 * 1024));

  function tryAdd(picked: FileList | null) {
    if (!picked || picked.length === 0) return;
    const next = [...files];
    for (const f of Array.from(picked)) {
      if (next.length >= maxFiles) {
        toast.error(t(`${i18nPrefix}errMax`, { max: maxFiles, defaultValue: `Max ${maxFiles} attachments` }));
        break;
      }
      if (!allowedTypes.includes(f.type)) {
        toast.error(t(`${i18nPrefix}errType`, "File type not allowed") + ` (${f.name})`);
        continue;
      }
      if (f.size > maxBytes) {
        toast.error(t(`${i18nPrefix}errTooLarge`, { max: maxMb, defaultValue: `Max ${maxMb} MB` }) + ` (${f.name})`);
        continue;
      }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    onChange(next);
  }

  function remove(idx: number) {
    onChange(files.filter((_, i) => i !== idx));
  }

  const atLimit = files.length >= maxFiles;

  // Generate stable object URLs for image previews; revoke them when files
  // change or on unmount to avoid leaking memory.
  const previews = useMemo(() => {
    return files.map((f) => f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  }, [files]);
  useEffect(() => {
    return () => {
      previews.forEach((u) => { if (u) URL.revokeObjectURL(u); });
    };
  }, [previews]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {files.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px",
              background: "var(--surface-2)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              fontSize: 13,
            }}>
              {previews[i] ? (
                <img
                  src={previews[i]!}
                  alt=""
                  style={{
                    width: 44, height: 44, borderRadius: 6,
                    objectFit: "cover",
                    border: "1px solid var(--border)",
                    flex: "none",
                    background: "var(--surface)",
                  }}
                />
              ) : (
                <span style={{
                  display: "grid", placeItems: "center",
                  width: 44, height: 44, borderRadius: 6,
                  background: "var(--surface)",
                  color: "var(--ink-3)", flex: "none",
                  border: "1px solid var(--border)",
                }}>
                  {f.type === "application/pdf" ? <Ic.pdf /> : <Ic.image />}
                </span>
              )}
              <span style={{ flex: 1, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.name}
              </span>
              <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                {(f.size / 1024).toFixed(0)} KB
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                aria-label={t("action.remove", "Remove")}
                style={{
                  background: "transparent", border: "none",
                  color: "var(--ink-3)", cursor: "pointer",
                  padding: 4, display: "grid", placeItems: "center",
                }}
              >
                <Ic.x />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          accept={allowedTypes.join(",")}
          multiple
          disabled={disabled || atLimit}
          onChange={(e) => {
            tryAdd(e.target.files);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="btn"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || atLimit}
        >
          <Ic.upload />
          {atLimit
            ? t(`${i18nPrefix}atLimit`, "Limit reached")
            : t(`${i18nPrefix}add`, "Add files")}
        </button>
        <small style={{ display: "block", marginTop: 6, color: "var(--ink-3)", fontSize: 12 }}>
          {fileTypesHint ?? t(`${i18nPrefix}fileTypes`, { maxAttachments: maxFiles, defaultValue: `JPG, PNG, PDF · max ${maxMb} MB · up to ${maxFiles} files` })}
        </small>
      </div>
    </div>
  );
}
